import React, { useContext, useEffect, useState } from 'react'
import styles from "../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyles from "../../../../../assets/styles/global.module.scss";
import { Grid, TextField, Typography, useMediaQuery } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSearchParams } from 'react-router-dom';
import IconsColor from '../../../../../components/utility/iconsColor';
import { primaryColor } from '../../../../../utils/colors';
import { currentActiveChatbotThreadTypeNew, threadNotes } from '../../../../../models/axiosModel';
import { formatDateAccordingToTimezoneUsingMoment, hasInboxReadAccess } from '../../../../../utils/constants';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import GlobalButton from '../../../../../components/utility/globalButton/button';
import agent from '../../../../../services/api';
import GlobalDeletePopup from '../../../../../components/utility/globalDeletePopup/globalDeletePopup';
import { useGenericStore } from '../../../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import { renderUserAvatarDp } from '../../../renderUtils';
import { getEmptyThreadNoteObj } from '../../../utils';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import clsx from "clsx";

interface extendedThreadNotes extends threadNotes{
    flag_editable: boolean,
    updated_thread_note_value: string
}

const ThreadNoteComponent = () => {
    
    const { currentActiveChatbotThread, setCurrentActiveChatbotThread, setFlagOpenThreadInfoBlock, flagOpenThreadInfoBlock } = useRuleBasedInboxStore(useShallow((state) => ({
        currentActiveChatbotThread: state.currentActiveChatbotThread,
        setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread,
        setFlagOpenThreadInfoBlock: state.setFlagOpenThreadInfoBlock,
        flagOpenThreadInfoBlock: state.flagOpenThreadInfoBlock,
    })))

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const { userDetail, setFlagLoading, handleError, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload

    const [searchParams] = useSearchParams();
    const  chatbotUserId  = searchParams.get('chatbot_user_id') ?? (currentActiveChatbotThread ? currentActiveChatbotThread?.thread_id : "");
    const laptopScreen = useMediaQuery('(max-width : 1600px) and (min-width : 1000px)')

    const getDefaultValueOfThreadNotes = () => {
        if(currentActiveChatbotThread){
            return currentActiveChatbotThread?.thread_notes?.map((note) => {
                return {
                    ...note, 
                    flag_editable: false,
                    updated_thread_note_value: note?.thread_note_value
                }
            });
        }
        return [];
    }
    const [threadNotesData, setThreadNotesData] = useState<extendedThreadNotes[]>(getDefaultValueOfThreadNotes());
    const [showDeleteNoteConfirmation, setShowDeleteNoteConfirmation] = useState<boolean>(false);
    const [selectedNoteToDelete, setSelectedNoteToDelete] = useState<extendedThreadNotes | null>(null);

    const checkDisableAddNoteIcon = () => {
        const editabelNote = threadNotesData?.find((note) => note?.flag_editable);
        return editabelNote;
    }

    const addNewNote = () => {
        if(!checkDisableAddNoteIcon()){
            const newNote = getEmptyThreadNoteObj(userDetail);
            setThreadNotesData((prev) => [...prev, newNote]);
            scrollToBottom();
        }
    }

    const onChangeOfNoteValue = (value: string) => {
        setThreadNotesData((prev) => {
            return prev?.map((note) => {
                if(note?.flag_editable){
                    return {
                        ...note, 
                        thread_note_value: value,
                    }
                }
                return note;
            })
        })
    }

    const checkDisableSaveNoteBtn = () => {
        const editableNote = threadNotesData?.find((note) => note?.flag_editable);
        if(editableNote && editableNote?.thread_note_value?.trim()?.length){
            return false;
        } 
        return true;
    }

    const onClickOfCancel = () => {
        setThreadNotesData((prev) => {
            return prev?.filter((note) => note?.thread_note_id != "").map((note) => {
                return {
                    ...note, 
                    flag_editable: false,
                    thread_note_value: note.updated_thread_note_value
                }
            });
        })
    }

    const onClickOfSaveBtn = async (threadNotesData: extendedThreadNotes[], flagDeleteNote: boolean) => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        const payload = {
            thread_id: chatbotUserId ?? "",
            notes: threadNotesData?.map((note) => {
                return {
                    thread_note_id: note?.thread_note_id, 
                    thread_note_value: note?.thread_note_value,
                }
            })
        }

        const res = await agent.assignThreadNotes.put(payload);
        if(!(res.status)){
            handleError(res);
        } else {
            showSuccess(`Note ${flagDeleteNote ? "deleted" : "added"} successfully.`)
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        let updatedThread = currentActiveChatbotThread as currentActiveChatbotThreadTypeNew;
        updatedThread = {
            ...updatedThread,
            thread_notes: threadNotesData?.map((note) => {
                return {
                    ...note,
                    thread_note_timestamp: res?.data?.find((threadNote: any) => threadNote?.thread_note_id === note?.thread_note_id)?.timestamp ?? note?.thread_note_timestamp
                }
            })
        }
        setCurrentActiveChatbotThread(updatedThread);
    }

    useEffect(() => {
        if (currentActiveChatbotThread) {
            setThreadNotesData(getDefaultValueOfThreadNotes());
        } else {
            setThreadNotesData([]);
        }
    }, [currentActiveChatbotThread]);

    const handleEditNoteIconClick = (thread_note_id: string) => {
        if(!checkDisableAddNoteIcon()){
            setThreadNotesData((prev) => {
                return prev?.map((note) => {
                    if(note?.thread_note_id == thread_note_id){
                        return {
                            ...note, 
                            flag_editable: true,
                        }
                    }
                    return note;
                })
            });
        }
    }

    const handleDeleteNoteIconClick = (note: extendedThreadNotes) => {
        if(!checkDisableAddNoteIcon()){
            if(laptopScreen){
                setFlagOpenThreadInfoBlock(!flagOpenThreadInfoBlock);
            }
            setSelectedNoteToDelete(note);
            setShowDeleteNoteConfirmation(true);
        }
    }

    const deleteNote = () => {
        if(selectedNoteToDelete){
            const updatedThreadNoteData = threadNotesData?.filter((note) => note?.thread_note_id != selectedNoteToDelete?.thread_note_id);
            onClickOfSaveBtn(updatedThreadNoteData, true);
        }
    }

    const scrollToBottom = () => {
        const container = document.getElementById("infoTab");
        if (container) {
            setTimeout(() => {
                container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
            }, 100);
        }
    }

    const focusNoteTextField = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        const el = e.target;
        const length = el.value.length;
        el.setSelectionRange(length, length);
    }

    return (
        <>
            <Grid item mt={2} mb={1} className={clsx(styles.userLastMessageEntries, styles.threadTagComponentGrid, globalStyles.fulwidth)}>
                <Grid container direction={"column"}>
                    <Grid item xs={12} className={styles.threadTagTitleGrid}>
                        <Grid container gap={1} justifyContent={"space-between"}>
                            <Grid item className={styles.threadTagTitle}>
                                Notes
                            </Grid>
                            {hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail) && 
                                <Grid item>
                                    <span className={clsx(globalStyles.primaryColor, styles.addTagStyles, checkDisableAddNoteIcon() ? styles.disabledAddNoteStyles : globalStyles.clickable)} onClick={() => addNewNote()}>
                                        <IconsColor iconName='plus' width="15" height="15" fillColor={primaryColor} />
                                        Add
                                    </span>
                                </Grid>
                            }
                        </Grid>
                    </Grid>

                    <Grid item className={clsx(styles.messageEntryValue, styles.threadNotesCardGrid, globalStyles.fulwidth)} ml={0.5}>
                        {(threadNotesData?.length > 0)  ? 
                            <Grid container mt={0.5} className={globalStyles.fulwidth} gap={1} flexWrap={"wrap"}>
                                {threadNotesData?.map((note) => {
                                    if(note?.flag_editable){
                                        return (
                                            <Grid item xs={12}>
                                                <Grid container gap={1} direction={"column"}>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            multiline
                                                            autoFocus
                                                            className={styles.addNoteTextArea}
                                                            placeholder='Add a note...'
                                                            value={note?.thread_note_value}
                                                            onChange={(e) => onChangeOfNoteValue(e.target.value)}
                                                            onFocus={focusNoteTextField}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Grid container gap={1}>
                                                            <Grid item>
                                                                <GlobalButton
                                                                    className='primaryButtonStyle'
                                                                    buttonText="Save"
                                                                    sx={{ width: "80px" }}
                                                                    disabled={checkDisableSaveNoteBtn()}
                                                                    onClick={() => onClickOfSaveBtn(threadNotesData, false)}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                <GlobalButton
                                                                    className='borderedButtonStyle'
                                                                    buttonText="Cancel"
                                                                    sx={{ width: "80px" }}
                                                                    onClick={() => onClickOfCancel()}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                    } else {
                                        return <Grid item className={clsx(styles.notesCardGrid, globalStyles.fullwidth)}>
                                            <Grid container gap={1} className={globalStyles.fullwidth} direction={"column"} flexWrap={"nowrap"}>
                                                <Grid item xs={12} className={globalStyles.fullwidth}>
                                                    <Grid container flexWrap={"nowrap"} alignItems={"center"} gap={"6px"} justifyContent={"space-between"}>
                                                        <Grid item>
                                                            <Grid container gap={"6px"} alignItems={"center"}>
                                                                {renderUserAvatarDp(note?.performed_by_name, false)}
                                                                <Grid item>
                                                                    <Grid container direction={"column"}>
                                                                        <Typography className={styles.notePerformedByTypography}>{note?.performed_by_name}</Typography>
                                                                        <Typography className={styles.noteTimeStampTypography}>{formatDateAccordingToTimezoneUsingMoment(note?.thread_note_timestamp, userDetail.timezone_hour)}</Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        {((userDetail?.user_email == note?.performed_by_email) && hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail)) && 
                                                            <Grid item>
                                                                <Grid container gap={"4px"} alignItems={"center"}>
                                                                    <Grid item className={`${checkDisableAddNoteIcon() ? styles.disabledAddNoteStyles : globalStyles.clickable}`} onClick={() => handleEditNoteIconClick(note?.thread_note_id)}>
                                                                        <EditIcon className={styles.noteEditIcon} />
                                                                    </Grid>
                                                                    <Grid item className={`${checkDisableAddNoteIcon() ? styles.disabledAddNoteStyles : globalStyles.clickable}`} onClick={() => handleDeleteNoteIconClick(note)}>
                                                                        <DeleteIcon className={styles.noteDeleteIcon} />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    {note?.thread_note_value}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    }
                                })}
                            </Grid>
                            :
                            <Grid item className={styles.messageEntryValue}>
                                No notes available.
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </Grid>

            {showDeleteNoteConfirmation && 
                <GlobalDeletePopup
                    deleteDescription={`Are you sure want to delete this note`}
                    open={showDeleteNoteConfirmation}
                    onClose={() => {
                        setShowDeleteNoteConfirmation(false);
                        setSelectedNoteToDelete(null);
                    }}
                    onDelete={() => {
                        deleteNote();
                        setShowDeleteNoteConfirmation(false);
                        setSelectedNoteToDelete(null);
                    }}
                />
            }
        </>
    )
}

export default ThreadNoteComponent
